import React from 'react';
import ReactDOM from 'react-dom/client';
import './styles/index.css'; // ✅ Korrigierter Pfad zu styles/index.css
import App from './App';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter } from 'react-router-dom';
import { unregister as unregisterSW } from './serviceWorkerRegistration';
import './i18n'; // i18next initialisieren
import { VerbandProvider } from './context/VerbandContext';
import { enqueue as offlineEnqueue, flags as offlineFlags, labelFromUrl, darfPuffern } from './utils/offlineQueue';

// ─── Globaler Fetch-Interceptor ───────────────────────────────────────────────
// Fügt bei allen /api/-Requests automatisch den Auth-Header ein und
// leitet bei 401 global zur Login-Seite weiter.
// Enthält 429-Back-off: nach einem Rate-Limit werden API-Calls für 3s pausiert.
// Enthält Offline-Queue: NUR Wertungen und Kampfergebnisse (Positivliste in
//   utils/offlineQueue.js) werden bei Netzausfall in IndexedDB gelegt und bei
//   Reconnect nachgesendet. Jeder andere Schreibaufruf wird mit 503 und
//   klarer Meldung abgelehnt (18.10.2026): vorher bekam JEDER Aufruf ein
//   vorgetäuschtes {success:true}, auch Bracket erzeugen, Check-in und
//   Siegerehrung, und die Oberfläche zeigte einen Haken für nichts.
(function installFetchInterceptor() {
  const originalFetch = window.fetch.bind(window);

  // Back-off: Timestamp bis wann API-Calls blockiert werden
  let rateLimitedUntil = 0;
  let isRedirecting = false; // Verhindert mehrfache 401-Redirects bei parallelen Requests

  const QUEUE_METHODS = new Set(['POST', 'PUT', 'DELETE', 'PATCH']);

  // Wege, auf denen ein 401 eine falsche Eingabe meldet und keine abgelaufene
  // Sitzung: Anmeldung (Verein, Athlet, Kampfrichter, Staff, Einwaage),
  // zweiter Faktor und das Zuruecksetzen des Passworts.
  // 02.10.2026: + anmeldung/personal (gemeinsames Fenster, Aufgabe 132) und
  // personal/login — fehlte; ein falsches Passwort warf auf die Startseite.
  const ANMELDE_WEGE = /\/api\/(auth\/(login|2fa|refresh-token|forgot-password|reset-password)|athlete\/login|kampfrichter\/login|regstaff\/login|weighstaff\/login|personal\/login|anmeldung\/personal)/;

  window.fetch = async (input, options = {}) => {
    const urlStr = input instanceof Request ? input.url : String(input);
    const isApiRequest = urlStr.startsWith('/api/');

    if (isApiRequest) {
      // 429 Back-off: blockiere API-Calls bis Back-off abgelaufen
      const now = Date.now();
      if (now < rateLimitedUntil) {
        return new Response(
          JSON.stringify({ success: false, error: 'Zu viele Anfragen – kurz warten', code: 'RATE_LIMIT_BACKOFF' }),
          { status: 429, headers: { 'Content-Type': 'application/json' } }
        );
      }

      const token = localStorage.getItem('token');

      if (token) {
        let headers = options.headers || {};
        if (headers instanceof Headers) {
          headers = Object.fromEntries(headers.entries());
        }
        if (!headers['Authorization'] && !headers['authorization']) {
          options = {
            ...options,
            headers: { ...headers, 'Authorization': `Bearer ${token}` }
          };
        }
      }

      // Super-Admin Verband-Switch: ?verband_id anhängen
      const activeVerbandId = localStorage.getItem('activeVerbandId');
      if (activeVerbandId) {
        const urlObj = new URL(urlStr, window.location.origin);
        if (!urlObj.searchParams.has('verband_id')) {
          urlObj.searchParams.set('verband_id', activeVerbandId);
          input = urlObj.pathname + urlObj.search;
        }
      }
    }

    let response;
    try {
      response = await originalFetch(input, options);
    } catch (networkError) {
      // Echter Netzwerkfehler (kein Internet) — schreibbare API-Calls in Queue
      const method = (options.method || 'GET').toUpperCase();
      const ablehnen = (grund) => new Response(
        JSON.stringify({ success: false, offline: true, code: 'OFFLINE', error: grund, message: grund }),
        { status: 503, headers: { 'Content-Type': 'application/json' } }
      );
      if (isApiRequest && QUEUE_METHODS.has(method) && !offlineFlags.isReplaying) {
        if (!darfPuffern(urlStr, method)) {
          console.warn(`[OfflineQueue] ${method} ${urlStr} abgelehnt: keine Verbindung`);
          return ablehnen('Keine Verbindung zum Server: NICHT gespeichert. Bitte erneut versuchen, sobald das Netz wieder da ist.');
        }
        const body = typeof options.body === 'string' ? options.body : JSON.stringify(options.body || {});
        const label = labelFromUrl(urlStr, method);
        // Die Anmeldung von JETZT mitnehmen: beim Nachsenden später könnte im
        // selben Browser schon jemand anderes angemeldet sein.
        const h = options.headers || {};
        const auth = h.Authorization || h.authorization || null;
        try {
          await offlineEnqueue({ url: urlStr, method, body, label, auth });
        } catch (dbErr) {
          console.error('[OfflineQueue] IndexedDB-Fehler:', dbErr);
          return ablehnen('Keine Verbindung und der Zwischenspeicher dieses Geräts ist nicht nutzbar: NICHT gespeichert.');
        }
        window.dispatchEvent(new Event('offline-queue-changed'));
        console.warn(`[OfflineQueue] ${method} ${urlStr} gespeichert (${label})`);
        // Wertungs-Oberflächen sollen weiterarbeiten können; queued sagt, dass
        // es noch nicht beim Server ist (der Hinweis steht im OfflineBanner).
        return new Response(
          JSON.stringify({ success: true, queued: true, label, message: `${label} offline gespeichert, wird nachgesendet` }),
          { status: 200, headers: { 'Content-Type': 'application/json' } }
        );
      }
      throw networkError;
    }

    if (isApiRequest) {
      // 429-Back-off aktivieren: 3 Sekunden keine weiteren API-Calls
      if (response.status === 429) {
        rateLimitedUntil = Date.now() + 3000;
        console.warn('⏸ [RateLimit] 429 empfangen – API-Calls für 3s pausiert');
      }

      // Zwei-Faktor-Pflicht (26.09.2026, middleware/zweiFaktorPflicht.js):
      // ein Admin ohne zweiten Faktor bekommt überall 403 ZWEI_FAKTOR_PFLICHT
      // ausser bei der Einrichtung. Statt leerer Fehler auf jeder Seite: hin
      // zur Einrichtung.
      if (response.status === 403 && window.location.pathname !== '/sicherheit') {
        response.clone().json().then(d => {
          if (d && d.code === 'ZWEI_FAKTOR_PFLICHT' && !isRedirecting) {
            isRedirecting = true;
            window.location.href = '/sicherheit';
          }
        }).catch(() => {});
      }

      // Globales 401-Handling: Session abgelaufen → Logout (einmalig, kein Loop)
      //
      // ⚠️ NICHT fuer die Anmelde-Wege selbst. Dort heisst 401 "Passwort
      // falsch", nicht "Sitzung abgelaufen". Ohne diese Ausnahme warf jeder
      // Tippfehler im Passwort den Benutzer wortlos auf die Startseite: das
      // Anmeldefenster verschwand, bevor es die Fehlermeldung zeigen konnte.
      //
      // ⚠️ Auch NICHT auf den Hallenbildschirmen (25.09.2026). Dort läuft die
      // Seite unbeaufsichtigt auf einem externen Schirm; ein 401 hätte das
      // Token gelöscht — damit auch die Dashboard-Anmeldung im selben Browser —
      // und die Werbeseite auf den Hallenschirm gelegt. Die Seite zeigt statt
      // dessen selbst einen Hinweis.
      const aufAnzeige = /^\/kampfrichter-display\//.test(window.location.pathname);
      // Beim Nachsenden aus dem Offline-Puffer meldet ein 401 nur, dass DIESER
      // Eintrag nicht mehr gilt (replayAll verwirft und meldet ihn) — kein
      // Abmelden der aktuellen Sitzung.
      if (response.status === 401 && !ANMELDE_WEGE.test(urlStr) && !aufAnzeige && !offlineFlags.isReplaying) {
        const isHomePage = window.location.pathname === '/';
        if (!isHomePage && !isRedirecting) {
          isRedirecting = true;
          console.warn('🚪 [Auth] 401 – Session abgelaufen, automatischer Logout...');
          localStorage.removeItem('token');
          localStorage.removeItem('authToken');
          localStorage.removeItem('clubData');
          window.location.href = '/';
        }
      }
    }

    return response;
  };
})();
// ─────────────────────────────────────────────────────────────────────────────

// Iframe-Guard: Wenn die App in einem fremden iframe läuft (z.B. Dojo-SuperAdmin-Vorschau),
// keine React-App initialisieren → verhindert unnötige API-Calls gegen das Rate-Limit.
// Ausnahme (22.10.2026): die Vorschau der bisherigen Bildschirm-Optik in der
// Modus-Übersicht, nur aus derselben Herkunft und nur mit ?vorschau=1 auf
// /display/<Matte> oder /formen-display (zeigt Beispieldaten, ohne Verbindung).
const eigeneVorschau = (() => {
  try {
    return window.top.location.origin === window.location.origin
      && new URLSearchParams(window.location.search).get('vorschau') === '1'
      && /^\/(display\/\d+|formen-display)\/?$/.test(window.location.pathname);
  } catch { return false; }   // fremde Herkunft: Zugriff auf top.location wirft
})();
if (window !== window.top && !eigeneVorschau) {
  document.getElementById('root').innerHTML =
    '<div style="display:flex;align-items:center;justify-content:center;height:100vh;font-family:sans-serif;color:#888;font-size:14px;">events.tda-intl.org – bitte direkt öffnen</div>';
} else {
  const root = ReactDOM.createRoot(document.getElementById('root'));
  root.render(
    <BrowserRouter>
      <VerbandProvider>
        <App />
      </VerbandProvider>
    </BrowserRouter>
  );

  // Service Worker deaktiviert — Updates sollen sofort sichtbar sein.
  // Ausnahme (02.10.2026, Aufgabe 171): auf Geräten mit eingeschaltetem
  // Admin-Push bleibt er aktiv, sonst wäre das Abo sofort wieder weg. sw.js
  // hat keinen fetch-Handler und keinen Cache — Updates bleiben sofort sichtbar.
  let pushAktiv = false;
  try { pushAktiv = localStorage.getItem('tda-push-aktiv') === '1'; } catch { /* privat-Modus */ }
  if (!pushAktiv) unregisterSW();
}


// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();